import React from 'react';

const REMOTE_PATH = '/v1/api/pathways/pathways-support-ui/v1/assets/ui.js';

export function getEnvFromHostname() {
  const hostname = window.location.host;
  const envMap = [
    { env: 'dev', matcher: /^localhost/, },
    { env: 'dev', matcher: /^athos-dev/, },
    { env: 'staging', matcher: /^athos-staging\.mobilgov\.com$/, },
    { env: 'ca-staging', matcher: /^athos-staging\.payitgov\.ca$/, },
    { env: 'system', matcher: /^athos-system\.payitgov\.com$/, },
    { env: 'prod', matcher: /^athos\.mobilgov\.com$/, },
    { env: 'ca-prod', matcher: /^athos\.payitgov\.ca$/, },
  ];
  return envMap.find(m => m.matcher.test(hostname)).env;
}

export function getRemoteUrl() {
  const hostname = window.location.host;
  // TODO: confirm staging/system/prod hostnames with the pathways team.
  const envMap = [
    { host: 'service-api-dev.payitgov.com', matcher: /^localhost/, },
    { host: 'service-api-dev.payitgov.com', matcher: /-dev\.mobilgov\.com$/, },
    { host: 'service-api-staging.payitgov.com', matcher: /-staging\.mobilgov\.com$/, },
    { host: 'service-api-staging.payitgov.ca', matcher: /-staging\.payitgov\.ca$/, },
    { host: 'service-api-system.payitgov.com', matcher: /-system\.payitgov\.com$/, },
    { host: 'service-api.payitgov.com', matcher: /\.mobilgov\.com$/, },
    { host: 'service-api.payitgov.ca', matcher: /\.payitgov\.ca$/, },

    // fallbacks ... when in doubt, use prod.
    { host: 'service-api.payitgov.com', matcher: /\.com$/, },
    { host: 'service-api.payitgov.ca', matcher: /\.ca$/, },
  ];
  const host = envMap.find(m => m.matcher.test(hostname)).host;
  return `https://${host}${REMOTE_PATH}`;
}

/*
 * This function does the work to load a federated module from the
 * pathways-support-ui remote which contains all the UI. The remote
 * uses Vite for build/module federation.
 *
 * All this is necessary to make WebPack module federation (used by
 * Athos) play nice with vite's module federation.
 *
 * The real challenge here is that we have to specify the remote URL
 * dynamically so we get the one from the right environment. Webpack
 * supports that (sorta-kinda), but it’s not compatible with the way
 * vite federates modules.
 */
export async function loadRemote() {
  const container = await loadScript(getRemoteUrl());

  // Initialize the module/sharing
  await __webpack_init_sharing__('default'); // eslint-disable-line no-undef
  await container.init(__webpack_share_scopes__.default); // eslint-disable-line no-undef

  // now unpack the result and return it the way that React.lazy expects.
  const factory = await container.get('./PathwaysSupportUi');
  const componentFunctionOrClass = factory();
  return componentFunctionOrClass;

  async function loadScript(url) {
    let module;
    // Step one: mount the remote module in a <script> tag & wait for it
    // to load.
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = url;
      script.async = true;
      script.onload = resolve;
      script.onerror = e => reject(e);
      document.head.appendChild(script);
    }).catch(() => {
      // If the load fails, return a mock module that contains a basic error
      // component.
      module = {
        get: () => {
          return () => () => {
            return (
              <div>
                Could not load the Stepped Pathways UI module.
              </div>
            );
          };
        },
        init: () => {},
        dynamicLoadingCss: () => {},
      };
    });

    if (module) {
      return module;
    }

    // Step 2: create a second script tag that references the first and
    // attaches its contents to `window.payitPathwaysModuleContents`. This
    // mimics how WebPack expects federated modules to work.
    const script = document.createElement('script');
    script.type = 'module';
    const contents = `import * as container from '${url}'; window.payitPathwaysModuleContents = container;`;
    script.innerText = contents;
    document.head.appendChild(script);

    // Step 3: wait for step 2 to finish.
    while (typeof window.payitPathwaysModuleContents === 'undefined') {
       
      await new Promise(resolve => {
        setTimeout(resolve, 10);
      });
    }

    // step 4: return the module contents and clean up.
    module = window.payitPathwaysModuleContents;
    delete window.payitPathwaysModuleContents;
    return module;
  }
}
